import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useDeclarations from 'hooks/useDeclarations';
import useProducts from 'hooks/useProducts';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ParsedCard } from 'store/declaration-form-errors/ParsedForm';
import { kebabCase } from 'lodash';
import { NavItem } from './DeclarationVerticalNavigation';
import useProductTemplateFormErrors from 'hooks/useProductTemplateFormErrors';
import { clearFlash, doFlash } from 'views/declarations/utils/form-utils';
import { StyledSteps } from '../Form.styles';

interface Props {
    navs?: NavItem[];
    active?: number;
    setActive?: (index: number) => void;
}

const VerticalNav: FC<Props> = ({ navs, setActive: setActiveProp, active: activeProp }) => {
    const {
        productId,
        consignmentId,
        type: productTemplateType,
    } = useParams<{ productId: string; consignmentId: string; type: string }>();
    const { declarationErrors } = useDeclarationFormErrors();
    const { productTemplateErrors } = useProductTemplateFormErrors();

    const { products } = useProducts();
    const { declaration } = useDeclarations();

    const [active, setActive] = useState(activeProp);
    const [clicked, setClicked] = useState(false);
    const [wasClicked, setWasClicked] = useState(false);

    const handleClick = useCallback(
        (elementId: string, index: number) => {
            setClicked(true);

            const element = document.getElementById(elementId);

            if (element !== null) {
                setActiveProp?.(index);
                setActive(index);

                // Remove flashing if present
                clearFlash();

                // Scroll to element
                element.offsetParent?.scrollTo({
                    top: element.offsetTop - 20, // Add 20 pixels above the element so it is better in view.
                });

                // Add a flash animation
                doFlash(element);
            }
        },
        [setActiveProp]
    );

    /**
     * Click the most bottom option while the cards section is not scrolled to the bottom.
     * The click says that the selected must be clicked one. But upon clicking, it scrolls
     * the cards section which causes for the `activeProp` to be changed which would in turn
     * change the active nav to the most top visible one. But that is not what the user has selected.
     *
     * This useEffect fixes that issue.
     */
    useEffect(() => {
        // Don't clear flash if active section is changed by the on click event
        // and reset the clicked to false to reset it
        // This double protection is needed to counter buggy interaction between nav bar and sections.
        if (clicked) {
            setClicked(false);
            setWasClicked(true);
            return;
        }
        if (wasClicked) {
            setWasClicked(false);
            return;
        }

        setActive(activeProp);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProp]);

    const productIndex = useMemo(
        () => (products?.list as any).findIndex(({ id }: { id: string }) => id === productId) + 1,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [productId]
    );

    /**
     * ICS2 house consignments are numbered by their place in the declaration, which is the same
     * order the parser reports them in.
     */
    const houseConsignmentIndex = useMemo(() => {
        const houseConsignments =
            declaration?.ics2ENSDeclaration?.payload?.consignmentMasterLevel?.consignmentHouseLevel ?? [];

        return houseConsignments.findIndex(({ id }) => id === consignmentId) + 1;
    }, [consignmentId, declaration?.ics2ENSDeclaration]);

    /**
     * A goods item of a house consignment is numbered by its place in the consignment holding it,
     * which is the order the parser reports the items of that consignment in.
     */
    const houseGoodsItemIndex = useMemo(() => {
        const houseConsignments =
            declaration?.ics2ENSDeclaration?.payload?.consignmentMasterLevel?.consignmentHouseLevel ?? [];
        const goodsItems = houseConsignments.find(({ id }) => id === consignmentId)?.goodsItem ?? [];

        return goodsItems.findIndex(({ id }) => id === productId) + 1;
    }, [consignmentId, declaration?.ics2ENSDeclaration, productId]);

    const houseConsignmentErrors = useMemo(
        () => declarationErrors.houseConsignments?.find(({ index }) => index === houseConsignmentIndex),
        [declarationErrors.houseConsignments, houseConsignmentIndex]
    );

    const errors: Set<string> | undefined = useMemo(() => {
        if (consignmentId && productId) {
            const goodsItem = houseConsignmentErrors?.items?.find(({ index }) => index === houseGoodsItemIndex);

            return extractNamesFromErrors(goodsItem?.errors ?? []);
        }

        if (consignmentId) {
            return extractNamesFromErrors(houseConsignmentErrors?.errors ?? []);
        }

        if (productId) {
            // ICS2 reports its master items on their own; every other declaration type has one item list.
            const itemErrors = declarationErrors.masterItems?.length
                ? declarationErrors.masterItems
                : declarationErrors.items;

            return extractNamesFromErrorsIndexed(itemErrors)[productIndex];
        }

        if (productTemplateType) {
            return extractNamesFromErrors(productTemplateErrors.template);
        }

        return extractNamesFromErrors(declarationErrors.masterDetails);
    }, [
        consignmentId,
        declarationErrors.items,
        declarationErrors.masterDetails,
        declarationErrors.masterItems,
        houseConsignmentErrors,
        houseGoodsItemIndex,
        productTemplateErrors.template,
        productTemplateType,
        productId,
        productIndex,
    ]);

    return (
        <StyledSteps
            progressDot
            current={active}
            direction="vertical"
            items={navs?.map(({ title }) => ({
                title,
                className: errors?.has(title) ? 'step-error' : '',
            }))}
            itemDetails={!!productId}
            onChange={(id: number) => navs && handleClick(`${kebabCase(navs[id]?.title)}-form-card`, id)}
        />
    );
};
export default VerticalNav;

// Cards of a list - `Seal 1`, `Packages 2` - are reported one per entry, while the navigation lists
// them once under the name they share, so the number is dropped along with the space before it.
const cardNameOfError = ({ name }: ParsedCard) => name.replace(/ ?\d+/g, '');

const extractNamesFromErrors = (arrErrors: ParsedCard[]) => {
    return arrErrors.reduce<Set<string>>((acc, error) => {
        acc.add(cardNameOfError(error));
        return acc;
    }, new Set());
};

const extractNamesFromErrorsIndexed = (productErrors: any[]) => {
    const names: { [index: number]: Set<string> } = {};

    productErrors.forEach(({ index, errors }) => {
        names[index] = extractNamesFromErrors(errors);
    });

    return names;
};
